import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

// NOTE: localStorage is used here intentionally.
// Cookie-consent preference is a non-sensitive boolean flag (accepted/declined)
// that must be readable synchronously by the GA disable check below.
// An httpOnly cookie cannot be read from JS by design and would break that flow.
// No PII or auth data is stored — this is safe and consistent with ICO/GDPR guidance
// for storing consent state.
const COOKIE_CONSENT_KEY = "yit_cookie_consent";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const consent = window.localStorage.getItem(COOKIE_CONSENT_KEY);
      if (!consent) {
        setVisible(true);
      }
    } catch {
      // localStorage may be unavailable (private mode / SSR) — show banner safely.
      setVisible(true);
    }
  }, []);

  const accept = () => {
    try {
      window.localStorage.setItem(COOKIE_CONSENT_KEY, "accepted");
    } catch {
      /* ignore storage errors */
    }
    setVisible(false);
  };

  const decline = () => {
    try {
      window.localStorage.setItem(COOKIE_CONSENT_KEY, "declined");
    } catch {
      /* ignore storage errors */
    }
    setVisible(false);
    // Disable GA if user declines
    window["ga-disable-G-R1M0H3F7GG"] = true;
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6"
    >
      <div className="max-w-4xl mx-auto p-5 sm:p-6 rounded-2xl bg-gray-900 border border-gray-700 shadow-2xl flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex-1">
          <p className="text-gray-300 text-sm leading-relaxed">
            We use cookies to analyse website traffic and improve performance.
            By clicking "Accept", you consent to the use of analytics cookies. See our{" "}
            <Link to="/privacy" className="text-cyan-400 hover:text-cyan-300 underline">
              Privacy Policy
            </Link>{" "}
            for more details.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <Button
            data-testid="cookie-decline-btn"
            variant="outline"
            size="sm"
            onClick={decline}
            className="border-gray-600 text-gray-300 hover:bg-gray-800 text-xs px-4"
          >
            Decline
          </Button>
          <Button
            data-testid="cookie-accept-btn"
            size="sm"
            onClick={accept}
            className="btn-primary text-white text-xs px-5"
          >
            Accept
          </Button>
        </div>
      </div>
    </div>
  );
}
